import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../config';
import { syncSentryUser } from '../monitoring/sentry';

interface User {
  username: string;
  roles: string[];
  firstName?: string;
  lastName?: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    syncSentryUser(user ? { username: user.username } : null);
  }, [user]);

  const checkAuth = async () => {
    try {
      let response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        const rememberResponse = await fetch(`${API_BASE_URL}/api/auth/remember`, {
          method: 'POST',
          credentials: 'include',
        });

        if (rememberResponse.ok) {
          response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            credentials: 'include',
          });
        }
      }

      if (response.ok) {
        const data = await response.json();
        setUser({
          username: data.user,
          roles: data.roles,
          firstName: data.firstName,
          lastName: data.lastName,
          avatar: data.avatar,
        });
      } else {
        setUser(null);
      }
    } catch (err) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string, rememberMe = false) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, rememberMe }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser({ username: data.user, roles: data.roles });
      } else {
        const data = await response.json();
        setError(data.message || 'Грешни данни за вход');
        throw new Error(data.message || 'Грешни данни за вход');
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Грешка при вход');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async (credential: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          username: data.user,
          roles: data.roles,
          firstName: data.firstName,
          lastName: data.lastName,
          avatar: data.avatar,
        });
      } else {
        const data = await response.json();
        setError(data.message || 'Грешка при вход с Google');
        throw new Error(data.message || 'Грешка при вход с Google');
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Грешка при вход с Google');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      // Ignore errors on logout
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        googleLogin,
        logout,
        refreshUser: checkAuth,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
